<script setup lang="ts">
import { ref, onMounted, nextTick, watch } from 'vue'

interface VideoGalleryItemProps {
  videoSrc?: string | null
  posterSrc: string
  title?: string
}

const props = withDefaults(defineProps<VideoGalleryItemProps>(), {
  videoSrc: null,
  title: '',
})

const isHovered = ref(false)
const isTouch = ref(false)
const isPlaying = ref(false)
const videoRef = ref<HTMLVideoElement | null>(null)
const imageLoaded = ref(false)
const videoLoaded = ref(false)
const posterImageRef = ref<HTMLImageElement | null>(null)
const mainImageRef = ref<HTMLImageElement | null>(null)

const playVideo = (): void => {
  if (!videoRef.value) return
  videoRef.value
    .play()
    .then(() => {
      isPlaying.value = true
    })
    .catch((err) => {
      console.error('Ошибка воспроизведения:', err)
    })
}

const pauseVideo = (): void => {
  if (!videoRef.value) return
  videoRef.value.pause()
  videoRef.value.currentTime = 0
  isPlaying.value = false
}

const handleVideoPlay = (): void => {
  isPlaying.value = true
}

const handleVideoPause = (): void => {
  isPlaying.value = false
}

const handleImageLoad = (): void => {
  imageLoaded.value = true
}

const handleVideoLoadedData = (): void => {
  videoLoaded.value = true
}

const handleImageError = (): void => {
  imageLoaded.value = true // Скрываем прелоадер даже при ошибке
}

const handleVideoError = (): void => {
  videoLoaded.value = true // Скрываем прелоадер даже при ошибке
}

const isLoading = (): boolean => {
  return !imageLoaded.value
}

const handleMouseEnter = (): void => {
  if (isTouch.value) return
  isHovered.value = true
  playVideo()
}

const handleMouseLeave = (): void => {
  if (isTouch.value) return
  isHovered.value = false
  pauseVideo()
}

const handleClick = (): void => {
  if (!isTouch.value) return
  if (isPlaying.value) {
    pauseVideo()
  } else {
    playVideo()
  }
}

const checkImageLoaded = (): void => {
  const img = posterImageRef.value || mainImageRef.value
  if (img && img.complete && img.naturalHeight !== 0) {
    imageLoaded.value = true
  }
}

watch(
  () => props.posterSrc,
  () => {
    imageLoaded.value = false
    nextTick(() => {
      checkImageLoaded()
    })
  },
)

onMounted(() => {
  if (videoRef.value) {
    videoRef.value.muted = true
  }
  const hasHover = window.matchMedia('(hover: hover)').matches
  isTouch.value = !hasHover

  nextTick(() => {
    checkImageLoaded()
  })
})
</script>

<template>
  <div
    class="video-gallery-item"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @click="handleClick"
  >
    <div class="video-container">
      <div v-if="isLoading()" class="loader-container">
        <div class="loader"></div>
      </div>
      <video
        v-if="videoSrc"
        ref="videoRef"
        :src="videoSrc"
        :poster="posterSrc"
        class="video-element"
        :class="{ 'content-loaded': videoLoaded }"
        loop
        muted
        playsinline
        @play="handleVideoPlay"
        @pause="handleVideoPause"
        @ended="handleVideoPause"
        @loadeddata="handleVideoLoadedData"
        @error="handleVideoError"
      />
      <img
        v-if="videoSrc && !isPlaying"
        ref="posterImageRef"
        :src="posterSrc"
        :alt="title || ''"
        class="poster-overlay"
        :class="{ 'content-loaded': imageLoaded }"
        @load="handleImageLoad"
        @error="handleImageError"
      />
      <img
        v-else-if="!videoSrc"
        ref="mainImageRef"
        :src="posterSrc"
        :alt="title || ''"
        class="video-element"
        :class="{ 'content-loaded': imageLoaded }"
        @load="handleImageLoad"
        @error="handleImageError"
      />
    </div>
  </div>
</template>

<style scoped>
.video-gallery-item {
  text-align: center;
}

.video-container {
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  margin-bottom: 0.5rem;
}

.video-element {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.poster-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.3s ease;
  pointer-events: none;
  z-index: 1;
  opacity: 0;
}

.poster-overlay.content-loaded {
  opacity: 1;
}

.video-element {
  opacity: 0;
  transition: opacity 0.3s ease;
}

.video-element.content-loaded {
  opacity: 1;
}

.loader-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.05);
  z-index: 2;
}

.loader {
  width: 3rem;
  height: 3rem;
  border: 0.25rem solid rgba(0, 0, 0, 0.1);
  border-top-color: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
